import React, { ReactElement, useMemo, useState } from "react";
import Link from 'next/link';
import classNames from 'classnames';
import moment from 'moment';
import { BuyButton } from "@kursk/components/ui/BuyButton/BuyButton";
import { getAddress } from "@kursk/utils/getAddress";
import { getVenueId } from "@common/utils/entities";

import { isTicketsInfoAvailable } from "@common/utils/tickets";
import { IMovie, IScheduleGroup, IScheduleGroupSchedule } from "@common/redux/types/movie";
import styles from "./MovieScheduleRow.module.scss";

interface IProps {
  scheduleGroup: IScheduleGroup;
  movie: IMovie;
  filterDates: {
    start: number;
    end: number;
  }
}

function getActualSchedules(schedules: IScheduleGroupSchedule[]) {
  const notEipskSchedules = schedules.filter((i) => !i.fromEipsk);

  if (notEipskSchedules.length) {
    return { schedules: notEipskSchedules, fromEipsk: false };
  } else {
    return { schedules, fromEipsk: true };
  }
}

export const MovieScheduleRow = ({ scheduleGroup, movie, filterDates }: IProps): ReactElement => {
  const { venue, event, schedules: allSchedules } = scheduleGroup;

  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSeanceId, setSelectedSeanceId] = useState(null);

  const { schedules, fromEipsk } = getActualSchedules(allSchedules);

  const scheduleEvent = useMemo(() => {
    return movie.events.find((event) => event._id === scheduleGroup.event._id);
  }, [movie, scheduleGroup])

  const times = schedules
    .map(({ start, end, _id, ticketsInfo }) =>
      ({ start, end, _id,
        isDisabled: !isTicketsInfoAvailable(ticketsInfo) || moment(start).valueOf() < moment().valueOf()
      })
    )
    .sort((a, b) => a.start - b.start)

  const timesHidden = event.type === "schedules" || fromEipsk;

  const checkBuyButtonDisabled = () => {
    let isAllTimesDisabled = true;

    times.forEach((startTime) => {
      if (!startTime.isDisabled) {
        isAllTimesDisabled = false;
      }
    });

    if (isAllTimesDisabled) {
      return !scheduleEvent.actualExternalSaleLink && !scheduleEvent.externalSaleLink;
    } else {
      return !selectedTime;
    }
  };

  const onTimeSelect = (start, _id) => {
    if (selectedTime === start) {
      setSelectedTime(null);
      setSelectedSeanceId(null);
    } else {
      setSelectedTime(start);
      setSelectedSeanceId(_id);
    }
  };

  return (
    <div className={styles.row}>
      <div className={classNames(styles.columnPlace, timesHidden && styles.wide)}>
        <div className={styles.columnTitle}>Место проведения</div>
        {venue.place?._id && (
          <div className={styles.placeName}>
            <Link href={`/places/${venue.place._id}/${venue.place.sysName}`}>
              {venue.place?.name}
            </Link>
          </div>
        )}
        <div className={styles.placeAddress}>
          {getAddress(venue.place?.address || venue.location?.address)}
        </div>
      </div>
      {!timesHidden && (
        <div className={styles.columnTimes}>
          <div className={styles.columnTitle}>Время проведения</div>
          <div className={styles.timesWrap}>
            {times.map(({ start, end, _id, isDisabled }, index) => (
              <div
                key={`${start}_${end}_${index}`}
                onClick={() => !isDisabled && onTimeSelect(start, _id)}
                className={classNames(
                  styles.timeItem,
                  isDisabled && styles.timeItemDisabled,
                  selectedTime === start && styles.timeItemSelected,
                )}>
                {moment(start).format('HH:mm')}
              </div>
            )
            )}
          </div>
        </div>
      )}
      <div className={styles.columnBuyButton}>
        <BuyButton
          entityType="events"
          entityId={scheduleEvent._id}
          disabled={checkBuyButtonDisabled()}
          defaultDate={selectedTime}
          eventExtraProps={selectedSeanceId ? { eventType: 'seances', seancesId: selectedSeanceId } : {}}
          venueId={getVenueId(venue)}
          filterDates={filterDates}
          hintBackgroundTheme="light"
          position="schedule"
        />
      </div>
    </div>
  )
}
